export type DealStage = 'new' | 'contacted' | 'interested' | 'negotiating' | 'offer' | 'contract' | 'closed'

export type Deal = {
  id: string
  title: string
  owner: string
  value: number
  probability: number
  stage: DealStage
  lastAction: string
}

export type PropertyLane = 'acquisition' | 'rehab' | 'leasing' | 'stabilized'

export type PropertyCard = {
  id: string
  name: string
  address: string
  manager: string
  value: number
  lane: PropertyLane
}

export type PhaseStatus = 'Pending' | 'In Progress' | 'Complete' | 'Blocked'
export type EstimateStage = 'Draft' | 'Sent to GC' | 'Estimate Received' | 'Sent to Investor' | 'Approved' | 'Rejected'
export type InvoiceStatus = 'Not Submitted' | 'Pending Review' | 'Approved' | 'Paid' | 'Disputed'
export type ProjectStatus = 'Estimate' | 'Approved' | 'In Progress' | 'Punch List' | 'Complete' | 'On Hold'

export type Phase = {
  id: string
  name: string
  trade: string
  subcontractor: string
  startDate: string
  endDate: string
  status: PhaseStatus
  budget: number
  actual: number
  photos: number
  invoiceStatus: InvoiceStatus
  invoiceAmount: number
  notes: string
}

export type Project = {
  id: string
  name: string
  address: string
  type: 'Renovation' | 'Flip' | 'BRRR' | 'New Construction'
  status: ProjectStatus
  estimateStage: EstimateStage
  pm: string
  gc: string
  investor: string
  estimatedCost: number
  approvedBudget: number
  actualCost: number
  startDate: string
  completionDate: string
  phases: Phase[]
}

export type OperatorWorkspace = {
  deals: Deal[]
  properties: PropertyCard[]
  projects: Project[]
}

type WorkspaceUser = {
  id: string
  isDemo: boolean
  name: string
  sessionToken?: string
}

const WORKSPACE_STORAGE_PREFIX = 'realassist_operator_workspace'

export const INITIAL_DEALS: Deal[] = [
  {
    id: 'd1',
    title: 'Midtown Townhomes Buyer Side',
    owner: 'N. Clark',
    value: 1540000,
    probability: 72,
    stage: 'negotiating',
    lastAction: 'Counter package sent 2h ago',
  },
  {
    id: 'd2',
    title: 'Lakepoint Seller Listing',
    owner: 'A. Rivera',
    value: 980000,
    probability: 61,
    stage: 'offer',
    lastAction: 'Offer review scheduled tomorrow',
  },
  {
    id: 'd3',
    title: 'Broadway Duplex Offer',
    owner: 'J. Brooks',
    value: 640000,
    probability: 95,
    stage: 'contract',
    lastAction: 'Escrow docs signed',
  },
]

export const INITIAL_PROPERTIES: PropertyCard[] = [
  { id: 'p1', name: 'Maple Grove House', address: '142 Maple Grove Dr', manager: 'A. Rivera', value: 220000, lane: 'rehab' },
  { id: 'p2', name: 'Willow Creek House', address: '88 Willow Creek Blvd', manager: 'J. Brooks', value: 214000, lane: 'leasing' },
  { id: 'p3', name: 'Cedar Ridge House', address: '31 Cedar Ridge Ave', manager: 'S. Patel', value: 229000, lane: 'stabilized' },
  { id: 'p4', name: 'Harbor Duplex', address: '11 Harbor Way', manager: 'N. Clark', value: 318000, lane: 'acquisition' },
]

export const INITIAL_PROJECTS: Project[] = [
  {
    id: 'cp1', name: 'Maple Grove Full Renovation', address: '142 Maple Grove Dr, Austin TX',
    type: 'BRRR', status: 'In Progress', estimateStage: 'Approved',
    pm: 'Alex Rivera', gc: 'BuildRight LLC', investor: 'XL Property & Contracting',
    estimatedCost: 42000, approvedBudget: 44500, actualCost: 28600,
    startDate: '2026-06-15', completionDate: '2026-08-30',
    phases: [
      { id: 'ph1', name: 'Demo & Debris Removal', trade: 'General', subcontractor: 'ClearOut Crew', startDate: '2026-06-15', endDate: '2026-06-19', status: 'Complete', budget: 2800, actual: 2600, photos: 12, invoiceStatus: 'Paid', invoiceAmount: 2600, notes: 'Demo complete, debris hauled.' },
      { id: 'ph2', name: 'Framing & Structural', trade: 'Framing', subcontractor: 'FramePro Co.', startDate: '2026-06-20', endDate: '2026-07-01', status: 'Complete', budget: 6500, actual: 6800, photos: 8, invoiceStatus: 'Paid', invoiceAmount: 6800, notes: 'Added beam for open concept.' },
      { id: 'ph3', name: 'Rough Electrical', trade: 'Electrical', subcontractor: 'Volt Masters', startDate: '2026-07-02', endDate: '2026-07-08', status: 'Complete', budget: 3800, actual: 3800, photos: 6, invoiceStatus: 'Paid', invoiceAmount: 3800, notes: 'Panel upgraded to 200A.' },
      { id: 'ph4', name: 'Rough Plumbing', trade: 'Plumbing', subcontractor: 'PipeLine Pro', startDate: '2026-07-02', endDate: '2026-07-09', status: 'Complete', budget: 4200, actual: 4200, photos: 5, invoiceStatus: 'Paid', invoiceAmount: 4200, notes: 'Re-routed kitchen supply lines.' },
      { id: 'ph5', name: 'Insulation', trade: 'Insulation', subcontractor: 'InsulCo', startDate: '2026-07-10', endDate: '2026-07-12', status: 'Complete', budget: 1800, actual: 1800, photos: 4, invoiceStatus: 'Paid', invoiceAmount: 1800, notes: '' },
      { id: 'ph6', name: 'Drywall', trade: 'Drywall', subcontractor: 'Smooth Wall Inc.', startDate: '2026-07-14', endDate: '2026-07-22', status: 'In Progress', budget: 5500, actual: 0, photos: 3, invoiceStatus: 'Not Submitted', invoiceAmount: 0, notes: 'Hanging complete, taping in progress.' },
      { id: 'ph7', name: 'Flooring', trade: 'Flooring', subcontractor: 'FloorCraft TX', startDate: '2026-07-23', endDate: '2026-07-30', status: 'Pending', budget: 7200, actual: 0, photos: 0, invoiceStatus: 'Not Submitted', invoiceAmount: 0, notes: '' },
      { id: 'ph8', name: 'Paint & Interior Finish', trade: 'Paint', subcontractor: 'Urban Finish Co.', startDate: '2026-07-31', endDate: '2026-08-10', status: 'Pending', budget: 4800, actual: 0, photos: 0, invoiceStatus: 'Not Submitted', invoiceAmount: 0, notes: '' },
      { id: 'ph9', name: 'Final Inspection & Punch List', trade: 'General', subcontractor: 'BuildRight LLC', startDate: '2026-08-25', endDate: '2026-08-30', status: 'Pending', budget: 900, actual: 0, photos: 0, invoiceStatus: 'Not Submitted', invoiceAmount: 0, notes: '' },
    ],
  },
  {
    id: 'cp2', name: 'Willow Creek Kitchen + Bath', address: '88 Willow Creek Blvd, Austin TX',
    type: 'Renovation', status: 'Estimate', estimateStage: 'Sent to Investor',
    pm: 'Alex Rivera', gc: 'Premier Contractors', investor: 'XL Property & Contracting',
    estimatedCost: 18500, approvedBudget: 0, actualCost: 0,
    startDate: '2026-08-01', completionDate: '2026-09-15',
    phases: [
      { id: 'ph1', name: 'Kitchen Demo', trade: 'General', subcontractor: 'TBD', startDate: '2026-08-01', endDate: '2026-08-03', status: 'Pending', budget: 1200, actual: 0, photos: 0, invoiceStatus: 'Not Submitted', invoiceAmount: 0, notes: '' },
      { id: 'ph2', name: 'Kitchen Rough Plumbing', trade: 'Plumbing', subcontractor: 'TBD', startDate: '2026-08-04', endDate: '2026-08-06', status: 'Pending', budget: 2800, actual: 0, photos: 0, invoiceStatus: 'Not Submitted', invoiceAmount: 0, notes: '' },
      { id: 'ph3', name: 'Kitchen Rough Electrical', trade: 'Electrical', subcontractor: 'TBD', startDate: '2026-08-06', endDate: '2026-08-08', status: 'Pending', budget: 2200, actual: 0, photos: 0, invoiceStatus: 'Not Submitted', invoiceAmount: 0, notes: '' },
      { id: 'ph4', name: 'Drywall & Tile', trade: 'Drywall/Tile', subcontractor: 'TBD', startDate: '2026-08-10', endDate: '2026-08-18', status: 'Pending', budget: 4800, actual: 0, photos: 0, invoiceStatus: 'Not Submitted', invoiceAmount: 0, notes: '' },
      { id: 'ph5', name: 'Cabinets & Countertops', trade: 'Carpentry', subcontractor: 'TBD', startDate: '2026-08-19', endDate: '2026-08-28', status: 'Pending', budget: 5200, actual: 0, photos: 0, invoiceStatus: 'Not Submitted', invoiceAmount: 0, notes: '' },
      { id: 'ph6', name: 'Finish & Punch List', trade: 'General', subcontractor: 'TBD', startDate: '2026-09-08', endDate: '2026-09-15', status: 'Pending', budget: 2300, actual: 0, photos: 0, invoiceStatus: 'Not Submitted', invoiceAmount: 0, notes: '' },
    ],
  },
]

function cloneWorkspace(data: OperatorWorkspace): OperatorWorkspace {
  return JSON.parse(JSON.stringify(data)) as OperatorWorkspace
}

function looksLikeDemoWorkspace(workspace: OperatorWorkspace): boolean {
  const hasDemoDeal = workspace.deals.some((deal) => deal.id === 'd1' || deal.title === 'Midtown Townhomes Buyer Side')
  const hasDemoProperty = workspace.properties.some((property) => property.id === 'p1' || property.name === 'Maple Grove House')
  const hasDemoProject = workspace.projects.some((project) => project.id === 'cp1' || project.name === 'Maple Grove Full Renovation')
  return hasDemoDeal || hasDemoProperty || hasDemoProject
}

function normalizeWorkspaceForUser(workspace: OperatorWorkspace, user: WorkspaceUser): OperatorWorkspace {
  if (user.isDemo) {
    return workspace
  }

  // Existing production users may have been seeded with demo data in earlier versions.
  if (looksLikeDemoWorkspace(workspace)) {
    return createStarterWorkspace(user.name)
  }

  return workspace
}

export function createDemoWorkspace(): OperatorWorkspace {
  return cloneWorkspace({
    deals: INITIAL_DEALS,
    properties: INITIAL_PROPERTIES,
    projects: INITIAL_PROJECTS,
  })
}

export function createStarterWorkspace(fullName: string): OperatorWorkspace {
  const firstName = fullName.trim().split(/\s+/)[0] || 'Operator'
  return {
    deals: [
      {
        id: 'starter-deal-1',
        title: `${firstName} BRRR Lead`,
        owner: firstName,
        value: 365000,
        probability: 28,
        stage: 'new',
        lastAction: 'Created from your new workspace',
      },
    ],
    properties: [
      {
        id: 'starter-property-1',
        name: `${firstName} Portfolio Starter`,
        address: 'Add your first property address',
        manager: firstName,
        value: 275000,
        lane: 'acquisition',
      },
    ],
    projects: [
      {
        id: 'starter-project-1',
        name: `${firstName} First BRRR Renovation`,
        address: 'Set your rehab property address',
        type: 'BRRR',
        status: 'Estimate',
        estimateStage: 'Draft',
        pm: fullName.trim() || firstName,
        gc: 'Assign contractor',
        investor: fullName.trim() || firstName,
        estimatedCost: 18500,
        approvedBudget: 0,
        actualCost: 0,
        startDate: '2026-07-15',
        completionDate: '2026-09-01',
        phases: [
          { id: 'starter-phase-1', name: 'Scope Walkthrough', trade: 'General', subcontractor: 'TBD', startDate: '2026-07-15', endDate: '2026-07-16', status: 'Pending', budget: 500, actual: 0, photos: 0, invoiceStatus: 'Not Submitted', invoiceAmount: 0, notes: 'Capture scope, budget, and vendor bids.' },
          { id: 'starter-phase-2', name: 'Bid Collection', trade: 'General', subcontractor: 'TBD', startDate: '2026-07-17', endDate: '2026-07-21', status: 'Pending', budget: 2500, actual: 0, photos: 0, invoiceStatus: 'Not Submitted', invoiceAmount: 0, notes: 'Collect GC and trade partner pricing.' },
        ],
      },
    ],
  }
}

function backendUnavailableError(message: string): boolean {
  return message.includes('Route not implemented') || message.includes('404') || message.includes('Persistent auth/workspace storage is not configured')
}

function readLocalWorkspaceForUser(userId: string, isDemo: boolean, fullName: string): OperatorWorkspace {
  const localUser: WorkspaceUser = {
    id: userId,
    isDemo,
    name: fullName,
  }

  if (typeof window === 'undefined') {
    const seed = isDemo ? createDemoWorkspace() : createStarterWorkspace(fullName)
    return normalizeWorkspaceForUser(seed, localUser)
  }

  const raw = window.localStorage.getItem(`${WORKSPACE_STORAGE_PREFIX}:${userId}`)
  if (!raw) {
    const seed = isDemo ? createDemoWorkspace() : createStarterWorkspace(fullName)
    return normalizeWorkspaceForUser(seed, localUser)
  }

  try {
    const parsed = JSON.parse(raw) as OperatorWorkspace
    if (!Array.isArray(parsed?.deals) || !Array.isArray(parsed?.properties) || !Array.isArray(parsed?.projects)) {
      const seed = isDemo ? createDemoWorkspace() : createStarterWorkspace(fullName)
      return normalizeWorkspaceForUser(seed, localUser)
    }
    const normalized = normalizeWorkspaceForUser(parsed, localUser)
    if (normalized !== parsed) {
      writeLocalWorkspaceForUser(userId, normalized)
    }
    return normalized
  } catch {
    const seed = isDemo ? createDemoWorkspace() : createStarterWorkspace(fullName)
    return normalizeWorkspaceForUser(seed, localUser)
  }
}

function writeLocalWorkspaceForUser(userId: string, workspace: OperatorWorkspace) {
  if (typeof window === 'undefined') {
    return
  }
  window.localStorage.setItem(`${WORKSPACE_STORAGE_PREFIX}:${userId}`, JSON.stringify(workspace))
}

export async function readWorkspaceForUser(user: WorkspaceUser): Promise<OperatorWorkspace> {
  try {
    if (!user.sessionToken) {
      throw new Error('Persistent auth/workspace storage is not configured.')
    }

    const response = await fetch('/api/workspace', {
      headers: {
        Authorization: `Bearer ${user.sessionToken}`,
      },
    })

    if (!response.ok) {
      const body = await response.json().catch(() => null)
      throw new Error(typeof body?.detail === 'string' ? body.detail : `Request failed with ${response.status}`)
    }

    const workspace = (await response.json()) as OperatorWorkspace
    const normalized = normalizeWorkspaceForUser(workspace, user)

    if (normalized !== workspace) {
      // Heal server state once for accounts that were incorrectly seeded with demo data.
      await fetch('/api/workspace', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.sessionToken}`,
        },
        body: JSON.stringify(normalized),
      })
    }

    writeLocalWorkspaceForUser(user.id, normalized)
    return normalized
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Workspace load failed'
    if (!backendUnavailableError(message) && !message.includes('Unauthorized')) {
      throw error
    }
    return readLocalWorkspaceForUser(user.id, user.isDemo, user.name)
  }
}

export async function writeWorkspaceForUser(user: WorkspaceUser, workspace: OperatorWorkspace): Promise<void> {
  writeLocalWorkspaceForUser(user.id, workspace)

  try {
    if (!user.sessionToken) {
      throw new Error('Persistent auth/workspace storage is not configured.')
    }

    const response = await fetch('/api/workspace', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.sessionToken}`,
      },
      body: JSON.stringify(workspace),
    })

    if (!response.ok) {
      const body = await response.json().catch(() => null)
      throw new Error(typeof body?.detail === 'string' ? body.detail : `Request failed with ${response.status}`)
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Workspace save failed'
    if (!backendUnavailableError(message) && !message.includes('Unauthorized')) {
      throw error
    }
  }
}